import {
  ContractInvoiceType,
  ContractStatus,
} from "@boark/shared/types/contract";
import { ContractCreate } from "@boark/shared/types/contract-create";
import { ContractUpdate } from "@boark/shared/types/contract-update";
import {
  ContractWithCounterParty,
  contractWithCounterPartySchema,
} from "@boark/shared/types/contract-with-counter-party";
import * as z from "zod";

import {
  deleteRequest,
  getRequest,
  postRequest,
  putRequest,
} from "../utils/request";

const API_BASE_URL = process.env.API_BASE_URL || "/api";

export function parseContractTagsInput(input: string): string[] {
  return input
    .split(",")
    .map((tag) => tag.trim())
    .filter((tag) => tag.length > 0);
}

/** Trim, drop empties, dedupe case-insensitively (keeps first spelling). */
export function normalizeContractFormTags(tags: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const raw of tags) {
    const t = raw.trim();
    if (t === "") {
      continue;
    }
    const key = t.toLowerCase();
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    result.push(t);
  }
  return result;
}

export function uniqueSortedTagSuggestions(
  contracts: { tags: string[] }[] | undefined,
): string[] {
  if (!contracts?.length) {
    return [];
  }
  const unique = new Set<string>();
  for (const contract of contracts) {
    for (const tag of contract.tags) {
      const trimmed = tag.trim();
      if (trimmed !== "") {
        unique.add(trimmed);
      }
    }
  }
  return [...unique].sort((left, right) => left.localeCompare(right));
}

export function contractFormValuesToCreate(values: {
  title: string;
  description: string;
  tags: string[];
  status: ContractStatus;
  cost: string;
  invoiceType: ContractInvoiceType | "";
  fromDate: string;
  toDate: string;
  counterPartyId: string;
}): ContractCreate {
  return {
    title: values.title.trim(),
    ...(values.description.trim() !== ""
      ? { description: values.description.trim() }
      : {}),
    tags: normalizeContractFormTags(values.tags),
    status: values.status,
    ...(values.cost.trim() !== "" ? { cost: Number(values.cost) } : {}),
    ...(values.invoiceType !== "" ? { invoiceType: values.invoiceType } : {}),
    fromDate: new Date(values.fromDate),
    ...(values.toDate.trim() !== "" ? { toDate: new Date(values.toDate) } : {}),
    ...(values.counterPartyId !== ""
      ? { counterPartyId: values.counterPartyId }
      : {}),
  };
}

export function contractFormValuesToUpdate(values: {
  title: string;
  description: string;
  tags: string[];
  status: ContractStatus;
  cost: string;
  invoiceType: ContractInvoiceType | "";
  fromDate: string;
  toDate: string;
  counterPartyId: string;
}): ContractUpdate {
  return {
    title: values.title.trim(),
    description:
      values.description.trim() === "" ? null : values.description.trim(),
    tags: normalizeContractFormTags(values.tags),
    status: values.status,
    cost: values.cost.trim() === "" ? null : Number(values.cost),
    invoiceType: values.invoiceType === "" ? null : values.invoiceType,
    fromDate: new Date(values.fromDate),
    toDate: values.toDate.trim() === "" ? null : new Date(values.toDate),
    counterPartyId: values.counterPartyId === "" ? null : values.counterPartyId,
  };
}

export async function getContracts(): Promise<ContractWithCounterParty[]> {
  return getRequest(
    `${API_BASE_URL}/contract`,
    z.array(contractWithCounterPartySchema),
  );
}

export async function getContract(
  id: string,
): Promise<ContractWithCounterParty> {
  return getRequest(
    `${API_BASE_URL}/contract/${id}`,
    contractWithCounterPartySchema,
  );
}

export async function createContract(
  contract: ContractCreate,
): Promise<ContractWithCounterParty> {
  return postRequest(
    `${API_BASE_URL}/contract`,
    contract,
    contractWithCounterPartySchema,
  );
}

export async function updateContract(
  id: string,
  contract: ContractUpdate,
): Promise<ContractWithCounterParty> {
  return putRequest(
    `${API_BASE_URL}/contract/${id}`,
    contract,
    contractWithCounterPartySchema,
  );
}

export async function deleteContract(id: string): Promise<void> {
  return deleteRequest(`${API_BASE_URL}/contract/${id}`);
}
